/**
 * Helpers purs pour le calcul de solde de congés.
 *
 * Séparation des responsabilités :
 *   - Ces utilitaires ne parlent pas à Supabase
 *   - Ils prennent quotas + leaves en paramètres et calculent les soldes
 *   - Testables unitairement (voir __tests__/leaveBalanceUtils.test.ts)
 *
 * Règle métier choisie :
 *   - 1 jour de congé = 1 jour calendaire (samedi/dimanche/férié inclus
 *     tels qu'ils sont déclarés dans leaves.start_date/end_date).
 *     Si plus tard on veut différencier jours ouvrés, helper séparé.
 *   - Un leave validé consomme le solde ; un leave pending est "provisoire"
 *     (affichage double).
 */

export type LeaveType = 'cp' | 'maladie' | 'sans_solde' | 'formation' | 'autre' | string;
export type LeaveStatus = 'pending' | 'validated' | 'rejected';

export interface LeaveRecord {
  id: string;
  employee_id: string;
  type: LeaveType;
  start_date: string;  // ISO date 'YYYY-MM-DD'
  end_date: string;    // ISO date 'YYYY-MM-DD' (inclus)
  status: LeaveStatus;
}

export interface QuotaRecord {
  id: string;
  employee_id: string;
  type: LeaveType;
  period_start: string;  // 'YYYY-MM-DD'
  period_end: string;    // 'YYYY-MM-DD' (inclus)
  total_days: number;
  notes?: string | null;
}

export interface LeaveBalance {
  type: LeaveType;
  /** Quota defini (undefined si pas de quota pour ce type / cette période) */
  totalDays: number | undefined;
  /** Jours consommés (status=validated) dans la période */
  consumed: number;
  /** Jours en attente de validation (status=pending) */
  pending: number;
  /** Solde restant si quota défini : totalDays - consumed (ignore pending) */
  remaining: number | undefined;
  /** Solde "provisoire" : totalDays - consumed - pending
   *  (= ce qui reste si tous les pending sont validés) */
  provisional: number | undefined;
  /** Quota ayant servi au calcul (pour UI : "Quota 25j période 2026") */
  quotaId?: string;
  periodStart?: string;
  periodEnd?: string;
  notes?: string | null;
}

// ============================================================================
// Helpers de base
// ============================================================================

/**
 * Nombre de jours entre deux dates (inclusif).
 * Exemple : '2026-04-15' → '2026-04-17' = 3 jours.
 * Utilise UTC pour éviter les décalages de fuseau.
 */
export function calculateLeaveDays(startDate: string, endDate: string): number {
  const start = new Date(startDate + 'T00:00:00Z');
  const end = new Date(endDate + 'T00:00:00Z');
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  if (end < start) return 0;
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return diffDays + 1; // inclusif
}

/**
 * Retourne true si le leave chevauche (même partiellement) la période donnée.
 * Permet de détecter les congés "de la période" pour le décompte.
 */
export function leaveIntersectsPeriod(
  leave: Pick<LeaveRecord, 'start_date' | 'end_date'>,
  periodStart: string,
  periodEnd: string,
): boolean {
  return leave.start_date <= periodEnd && leave.end_date >= periodStart;
}

/**
 * Calcule le nombre de jours du leave QUI TOMBENT dans la période donnée
 * (en cas de chevauchement partiel — rare mais possible si quota juin→mai
 * et leave à cheval sur l'année de référence).
 */
export function daysInPeriod(
  leave: Pick<LeaveRecord, 'start_date' | 'end_date'>,
  periodStart: string,
  periodEnd: string,
): number {
  const effectiveStart = leave.start_date > periodStart ? leave.start_date : periodStart;
  const effectiveEnd = leave.end_date < periodEnd ? leave.end_date : periodEnd;
  if (effectiveStart > effectiveEnd) return 0;
  return calculateLeaveDays(effectiveStart, effectiveEnd);
}

// ============================================================================
// Calcul de solde
// ============================================================================

/**
 * Construit le solde pour un (employé, type, période) donné.
 * Si aucun quota correspondant → totalDays/remaining/provisional = undefined,
 * mais consumed/pending sont toujours calculés (compteur simple).
 */
export function computeBalance(
  leaves: LeaveRecord[],
  employeeId: string,
  type: LeaveType,
  quota: QuotaRecord | undefined,
  /** Si quota=undefined, on accepte un fallback periodStart/periodEnd pour
   *  quand même compter les jours dans une période (ex: année civile en cours). */
  fallbackPeriod?: { start: string; end: string },
): LeaveBalance {
  const period = quota
    ? { start: quota.period_start, end: quota.period_end }
    : fallbackPeriod;

  let consumed = 0;
  let pending = 0;

  if (period) {
    for (const l of leaves) {
      if (l.employee_id !== employeeId) continue;
      if (l.type !== type) continue;
      if (l.status === 'rejected') continue;
      if (!leaveIntersectsPeriod(l, period.start, period.end)) continue;

      const days = daysInPeriod(l, period.start, period.end);
      if (l.status === 'validated') consumed += days;
      else if (l.status === 'pending') pending += days;
    }
  }

  const totalDays = quota?.total_days;
  const remaining = totalDays !== undefined ? totalDays - consumed : undefined;
  const provisional = totalDays !== undefined ? totalDays - consumed - pending : undefined;

  return {
    type,
    totalDays,
    consumed,
    pending,
    remaining,
    provisional,
    quotaId: quota?.id,
    periodStart: period?.start,
    periodEnd: period?.end,
    notes: quota?.notes ?? null,
  };
}

/**
 * Période "année civile en cours" — fallback quand aucun quota défini
 * pour permettre d'afficher un compteur "X jours pris en 2026".
 */
export function currentYearPeriod(referenceDate: Date = new Date()): { start: string; end: string } {
  const y = referenceDate.getUTCFullYear();
  return { start: `${y}-01-01`, end: `${y}-12-31` };
}

/**
 * Construit tous les soldes d'un employé pour les types donnés.
 * - Pour chaque type : s'il existe un quota "actif" (la date ref est dans sa période),
 *   on l'utilise. Sinon on utilise l'année civile en cours.
 */
export function computeEmployeeBalances(
  leaves: LeaveRecord[],
  quotas: QuotaRecord[],
  employeeId: string,
  types: LeaveType[],
  referenceDate: Date = new Date(),
): LeaveBalance[] {
  const refIso = referenceDate.toISOString().slice(0, 10);
  const myQuotas = quotas.filter(q => q.employee_id === employeeId);

  return types.map(type => {
    // Quota actif : celui dont la période contient la date de référence.
    const active = myQuotas.find(q =>
      q.type === type && q.period_start <= refIso && q.period_end >= refIso,
    );
    return computeBalance(
      leaves,
      employeeId,
      type,
      active,
      active ? undefined : currentYearPeriod(referenceDate),
    );
  });
}

/**
 * Types de congés à afficher dans le suivi solde (UI secrétariat + employé).
 * Virginie (avril 2026) : "Tout le reste doit apparaitre" — sauf qu'on
 * n'avait pas de RTT dans OGER, donc on liste les 5 types actuels.
 */
export const DISPLAYED_LEAVE_TYPES: LeaveType[] = ['cp', 'formation', 'autre', 'maladie', 'sans_solde'];

/** Labels FR pour l'affichage */
export const LEAVE_TYPE_LABELS: Record<string, string> = {
  cp: 'Congés payés',
  formation: 'Formation',
  autre: 'Autre',
  maladie: 'Maladie',
  sans_solde: 'Sans solde',
};
